import { type FC, PropsWithChildren } from 'react';

import { useOrganizationsQuery } from '@/api/organizations/organizationsApi';
import { InternalUserDoesNotHaveAccessToConnectPage } from '@/auth/InternalUserDoesNotHaveAccessToConnectPage';
import { isInternalUser } from '@/config/utils';
import { isOnboardingUserInProgress } from '@/util/isOnNewUserPage';

import { NoAccessExternalUser } from './NoAccessExternalUser';

/**
 * Check if the user has access to the Connect platform. This includes
 * both internal and external users.
 *
 * It will display a "no access" page if they don't have access.
 *
 * All users can sign in to Connect with BankID/SSO successfully. It is the
 * HTTP call to fetch organizations that determine if the user has access to
 * the platform. If the HTTP call returns an error code, then the user does
 * not have access. A successful HTTP call will return the organizations the
 * user has access to.
 */
export const HasUserAccessToConnect: FC<PropsWithChildren> = ({ children }) => {
  const onboardingUserInProgress = isOnboardingUserInProgress();

  const organizationsQuery = useOrganizationsQuery(undefined, {
    // New users does not have access to any organizations yet
    // and thus this query will return HTTP 403, an error code.
    // Skip this query for new users to avoid getting the error.
    skip: onboardingUserInProgress,
  });

  if (onboardingUserInProgress) {
    // Should render the new user pages even if the user doesn't have access yet.
    return <>{children}</>;
  }

  if (organizationsQuery.isLoading) {
    // No need to display a loading state.
    // The calls should be fast enough that the user
    // does not notice the loading.
    return null;
  }

  if (organizationsQuery.error) {
    return isInternalUser() ? (
      <InternalUserDoesNotHaveAccessToConnectPage />
    ) : (
      <NoAccessExternalUser />
    );
  }

  return <>{children}</>;
};
